import type { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { SiteSectionCategoryId } from "@/data/common/ids";
import type { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import type { ApplianceCountByCategory, SectionCategoryReference } from "@/domains/v4b/site-section/site-section.types";
import type { IVirtualizedTreeNode } from "@voltalis/ui-kit/components";

import { filterTreeByStringQuery, matchesNameSearch, walkTreeByLevel } from "@voltalis/frontend-utils";

import { entries } from "@/utils/object";

export interface ISiteSectionApplianceFilterOption {
  category: EProgramApplianceCategory;
  count: number;
}

interface ISiteSectionsTreeFilter {
  searchQuery: string;
  selectedApplianceCategories?: EProgramApplianceCategory[];
  selectedSectionCategories: SiteSectionCategoryId[];
}

type TGenericSectionNode<NODE> = {
  data: {
    applianceCountByCategory?: ApplianceCountByCategory;
    category?: SectionCategoryReference;
  } | null;
  children: NODE[];
  name: string;
};
type TreeNode = IVirtualizedTreeNode<TSiteSectionsTreeData>;

function matchesAppliances<T extends { applianceCountByCategory?: ApplianceCountByCategory }>(
  data: T | null = null,
  categories: EProgramApplianceCategory[],
): boolean {
  return !!data && categories.every((category) => (data?.applianceCountByCategory?.[category] ?? 0) > 0);
}

function matchesSectionCategories<T extends { category?: SectionCategoryReference }>(
  data: T | null = null,
  categories: SiteSectionCategoryId[],
): boolean {
  return !!data?.category && categories.includes(data.category.id);
}

function buildTreeNodeFilters<NODE extends TGenericSectionNode<NODE>>(
  filters: ISiteSectionsTreeFilter,
): Array<(node: NODE) => boolean> {
  const treeNodeFilters: Array<(node: NODE) => boolean> = [];
  const maybeApplianceCategoryFilter = filters.selectedApplianceCategories;

  if (filters.searchQuery.trim()) {
    treeNodeFilters.push((node) => matchesNameSearch(node.name, filters.searchQuery));
  }

  if (maybeApplianceCategoryFilter && maybeApplianceCategoryFilter.length) {
    treeNodeFilters.push((node) => matchesAppliances(node.data, maybeApplianceCategoryFilter));
  }

  if (filters.selectedSectionCategories.length) {
    treeNodeFilters.push((node) => matchesSectionCategories(node.data, filters.selectedSectionCategories));
  }

  return treeNodeFilters;
}

function filterTreeStrictly<NODE extends TGenericSectionNode<NODE>>(
  nodes: NODE[],
  filters: Array<(node: NODE) => boolean>,
): NODE[] {
  if (!filters.length) return nodes;

  return nodes.flatMap((node) => {
    const children = filterTreeStrictly(node.children ?? [], filters);
    const matches = filters.every((filter) => filter(node));

    return matches ? [{ ...node, children: [] }, ...children] : children;
  });
}

function shouldFilterTreeStrictly(filters: ISiteSectionsTreeFilter): boolean {
  const applianceCategoryFilter = filters.selectedApplianceCategories;
  return (
    (applianceCategoryFilter && applianceCategoryFilter.length > 0) || filters.selectedSectionCategories.length > 0
  );
}

export function getAvailableApplianceFilters(siteSectionsTree?: TreeNode): ISiteSectionApplianceFilterOption[] {
  const counts = new Map<EProgramApplianceCategory, number>();

  for (const node of walkTreeByLevel(siteSectionsTree?.children)) {
    if (!node.data) continue;

    for (const [category, count] of entries(node.data.applianceCountByCategory)) {
      const categoryCount = count ?? 0;

      if (categoryCount > 0) {
        counts.set(category, (counts.get(category) ?? 0) + categoryCount);
      }
    }
  }

  return Array.from(counts.entries(), ([category, count]) => ({ category, count }));
}

export function filterSectionForest<NODE extends TGenericSectionNode<NODE>>(
  sectionForest: NODE[],
  filters: ISiteSectionsTreeFilter,
): NODE[] {
  return shouldFilterTreeStrictly(filters)
    ? filterTreeStrictly(sectionForest, buildTreeNodeFilters(filters))
    : filterTreeByStringQuery(sectionForest, filters.searchQuery, (node) => node.name);
}
